import BaseHeadline from "../../../components/general/BaseHeadline"
import ReviewCard from "./ReviewCard"
import { useRef, useState, useEffect } from "react"
import { THeading, TIndustryProjectAttributes } from "../../../types/index"
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps"
import { Carousel } from "antd"
import { motion } from "framer-motion"

type ClientReviewsType = {
    heading: THeading
    projectFeedbacks?: {
        data: {
            id: number
            attributes: TIndustryProjectAttributes
        }[]
    }
}

export default function ClientReviews(props: ClientReviewsType) {
    const carouselRef = useRef<HTMLDivElement | null>(null)
    const [activeIndex, setActiveIndex] = useState(0)

    useEffect(() => {
        const carousel = carouselRef.current
        if (!carousel) return

        const handleScroll = () => {
            const index = Math.round(carousel.scrollLeft / carousel.clientWidth)
            setActiveIndex(index)
        }

        carousel.addEventListener("scroll", handleScroll)
        return () => carousel.removeEventListener("scroll", handleScroll)
    }, [])

    const activeLocation = props.projectFeedbacks?.data[activeIndex]?.attributes.location

    const geoUrl = "/2_hoch_DE.geo.json"
    const longitude = props.projectFeedbacks?.data[activeIndex]?.attributes.location_longitude ?? 0
    const latitude = props.projectFeedbacks?.data[activeIndex]?.attributes.location_latitude ?? 0

    const markers: { markerOffset: number; name: string; coordinates: [number, number] }[] = [
        { markerOffset: -15, name: "Dresden", coordinates: [longitude, latitude] },
        { markerOffset: -15, name: "Leipzig", coordinates: [longitude, latitude] },
        { markerOffset: -15, name: "München", coordinates: [longitude, latitude] },
    ]

    return (
        <section className="w-full py-20 mb-20 md:mb-0 px-4 sm:px-8 xl:px-0">
            <div className="max-w-7xl mx-auto pb-10">
                <BaseHeadline headline={props.heading.headline} label={props.heading.label} />
            </div>
            <div className="md:flex md:justify-center md:gap-20 md:items-center relative">
                <div className="absolute -top-20 h-[45rem] my-8 lg:relative lg:top-0 opacity-50 lg:opacity-100">
                    <ComposableMap
                        projection="geoMercator"
                        projectionConfig={{
                            center: [10.45, 51.16],
                            scale: 3000,
                        }}
                        width={500}
                        height={660}
                        style={{ width: "100%", height: "100%" }}>
                        <Geographies geography={geoUrl}>
                            {({ geographies }) =>
                                geographies.map((geo) => (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        style={{
                                            default: {
                                                fill: "#1B1A1A",
                                                stroke: "#4E4E4E",
                                                strokeWidth: 0.75,
                                                outline: "none",
                                                transition: "fill 0.4s ease",
                                            },
                                            hover: {
                                                fill: "#1B1A1A",
                                                stroke: "#4E4E4E",
                                                strokeWidth: 0.75,
                                                outline: "none",
                                                transition: "fill 0.4s ease",
                                            },
                                            pressed: {
                                                fill: "#1B1A1A",
                                                stroke: "#4E4E4E",
                                                strokeWidth: 0.75,
                                                outline: "none",
                                                transition: "fill 0.4s ease",
                                            },
                                        }}
                                    />
                                ))
                            }
                        </Geographies>
                        {markers
                            .filter((marker) => marker.name.toLowerCase() === activeLocation?.toLowerCase())
                            .map(({ name, coordinates, markerOffset }) => (
                                <Marker key={name} coordinates={coordinates}>
                                    <g fill="#17ACF7" strokeLinecap="round" strokeLinejoin="round" transform="translate(-12, -24)">
                                        <defs>
                                            <radialGradient id="dotGlow" cx="50%" cy="50%" r="50%">
                                                <stop offset="0%" stopColor="#17ACF7" stopOpacity="0.6" />
                                                <stop offset="40%" stopColor="#17ACF7" stopOpacity="0" />
                                            </radialGradient>
                                        </defs>
                                        <circle cx="12" cy="20" r="20" fill="url(#dotGlow)" />
                                        <circle cx="12" cy="20" r="2" />
                                    </g>
                                    <motion.text 
                                    textAnchor="middle" 
                                    y={markerOffset} 
                                    className="font-semibold" 
                                    style={{ fill: "#f7f7f7" }}
                                    initial={{opacity: 0, y: markerOffset + 30}}
                                    animate={{opacity: 1, y: markerOffset + 15}}
                                    transition={{duration: 0.4}}
                                    >
                                    {name}
                                    </motion.text>
                                </Marker>
                            ))}
                    </ComposableMap>
                </div>

                <div className="flex flex-col gap-2 relative">
                    <Carousel className="max-w-xl carousel-left" draggable afterChange={setActiveIndex} autoplay autoplaySpeed={10000}>
                        {props.projectFeedbacks?.data.map((project, index) => (
                            <div className="w-full">
                                <ReviewCard
                                    key={index}
                                    reviewText={project.attributes.feedbackShort || ""}
                                    url={`/portfolio/${project.id}`}
                                    personName={project.attributes.feedback_person}
                                    personPositon={project.attributes.feedback_personPosition}
                                    company={project.attributes.companyName}
                                    location={project.attributes.location}
                                />
                            </div>
                        ))}
                    </Carousel>
                </div>
            </div>
        </section>
    )
}
